import { useState, useEffect, useCallback } from 'react';
import { 
  getOrganizationBySlug, 
  getApartmentsPublic,
  createDocument,
  updateDocument,
  sendEmail,
  sendSMS,
  Organization,
  Apartment
} from '../lib/api';
import { 
  Buildings,
  User,
  Plus,
  Trash,
  EnvelopeSimple,
  Phone,
  House,
  CheckCircle,
  SpinnerGap,
  Receipt,
  ArrowClockwise,
  WifiSlash
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

interface ResidentInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_invoice_recipient?: boolean;
}

interface PendingUpdate {
  id: string;
  organization_id: string;
  apartment_id: string;
  residents: ResidentInfo[];
  verification_token: string;
  access_key?: string;
  email_verified: boolean;
  created_at: string;
  expires_at: string;
}

interface StandalonePortalProps {
  slug: string;
}

// Success screen
function SuccessScreen({ 
  portalSettings, 
  organization, 
  onReset 
}: { 
  portalSettings: { header_image?: string; logo?: string };
  organization: Organization | null;
  onReset: () => void;
}) {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [onReset]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
      <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm flex flex-col">
        {portalSettings.header_image ? (
          <div className="h-32 sm:h-36 relative flex-shrink-0">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ) : (
          <div className="bg-primary h-16 flex-shrink-0" />
        )}
        
        <div className="flex-1 flex items-center justify-center p-5">
          <Card className="w-full text-center shadow-none border-0">
            <CardContent className="pt-6 pb-6 px-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" weight="fill" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">
                Tack för dina uppgifter!
              </h2>
              <p className="text-slate-600 mb-5 text-sm leading-relaxed">
                Tack, dina kontaktuppgifter har sparats.
              </p>
              <p className="text-xs text-slate-400">
                Sidan laddas om om {countdown} sekunder...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function StandalonePortal({ slug }: StandalonePortalProps) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Retry state for slow connections
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const MAX_RETRIES = 3;
  
  // Email verification state
  const [awaitingEmailCode, setAwaitingEmailCode] = useState(() => {
    return sessionStorage.getItem('portal_awaitingEmailCode') === 'true';
  });
  const [emailCode, setEmailCode] = useState('');
  const [expectedEmailCode, setExpectedEmailCode] = useState(() => {
    return sessionStorage.getItem('portal_expectedEmailCode') || '';
  });
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [pendingUpdateId, setPendingUpdateId] = useState<string | null>(() => {
    return sessionStorage.getItem('portal_pendingUpdateId') || null;
  });
  
  // Phone verification state
  const [askPhoneVerification, setAskPhoneVerification] = useState(false);
  const [awaitingPhoneCode, setAwaitingPhoneCode] = useState(() => {
    return sessionStorage.getItem('portal_awaitingPhoneCode') === 'true';
  });
  const [phoneCode, setPhoneCode] = useState('');
  const [expectedPhoneCode, setExpectedPhoneCode] = useState(() => {
    return sessionStorage.getItem('portal_expectedPhoneCode') || '';
  });
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [currentPhoneIndex, setCurrentPhoneIndex] = useState(0);
  const [phonesVerified, setPhonesVerified] = useState<string[]>([]);
  const [residentsWithPhone, setResidentsWithPhone] = useState<ResidentInfo[]>([]);

  // Form state
  const [selectedApartment, setSelectedApartment] = useState('');
  const [residents, setResidents] = useState<ResidentInfo[]>([
    { id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }
  ]);

  // Force mobile viewport
  useEffect(() => {
    // Force viewport for standalone/embedded mode
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      document.head.appendChild(viewport);
    }
    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover');
    
    // Force styles on html and body
    const html = document.documentElement;
    const body = document.body;
    
    html.style.cssText = 'width:100%!important;max-width:100vw!important;overflow-x:hidden!important;';
    body.style.cssText = 'width:100%!important;max-width:100vw!important;min-width:0!important;overflow-x:hidden!important;margin:0!important;padding:0!important;';
    
    html.classList.add('mobile-portal');
    body.classList.add('portal-mobile');
    
    // Also add to root element
    const root = document.getElementById('root');
    if (root) {
      root.style.cssText = 'width:100%!important;max-width:100vw!important;';
    }
    
    return () => {
      html.classList.remove('mobile-portal');
      body.classList.remove('portal-mobile');
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [slug]);

  // Retry function with exponential backoff
  const loadDataWithRetry = useCallback(async (attempt: number = 0): Promise<boolean> => {
    console.log(`StandalonePortal - loadData attempt ${attempt + 1}/${MAX_RETRIES + 1} for slug:`, slug);
    
    if (!slug) {
      console.log('StandalonePortal - no slug provided');
      return false;
    }

    try {
      console.log('StandalonePortal - fetching organization...');
      const org = await getOrganizationBySlug(slug);
      console.log('StandalonePortal - organization result:', org);
      
      if (!org) {
        // Null result could mean network error OR organization doesn't exist
        // We'll retry a few times before concluding it doesn't exist
        if (attempt < MAX_RETRIES) {
          return false; // Signal to retry
        }
        return false; // Give up after max retries
      }
      
      setOrganization(org);
      setConnectionError(false);

      console.log('StandalonePortal - fetching apartments...');
      const aptsResponse = await getApartmentsPublic(org.id);
      const apts = aptsResponse.data || aptsResponse;
      const sortedApts = [...(Array.isArray(apts) ? apts : [])].sort((a, b) => {
        const numA = a.apartment_number || '';
        const numB = b.apartment_number || '';
        return numA.localeCompare(numB, 'sv', { numeric: true, sensitivity: 'base' });
      });
      console.log('StandalonePortal - apartments:', sortedApts.length);
      setApartments(sortedApts);
      
      return true; // Success
    } catch (error) {
      console.error(`StandalonePortal - Failed to load portal data (attempt ${attempt + 1}):`, error);
      return false; // Signal to retry
    }
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    setNotFound(false);
    setConnectionError(false);
    setRetryCount(0);
    
    // Try loading with automatic retries
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      setRetryCount(attempt);
      
      const success = await loadDataWithRetry(attempt);
      
      if (success) {
        setLoading(false);
        return;
      }
      
      // If not successful and not the last attempt, wait before retrying
      if (attempt < MAX_RETRIES) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff: 1s, 2s, 4s, max 5s
        console.log(`StandalonePortal - Retrying in ${delay}ms...`);
        setIsRetrying(true);
        await new Promise(resolve => setTimeout(resolve, delay));
        setIsRetrying(false);
      }
    }
    
    // All retries exhausted
    console.log('StandalonePortal - All retries exhausted');
    setConnectionError(true);
    setNotFound(true);
    setLoading(false);
  };

  // Manual retry function
  const handleManualRetry = () => {
    loadData();
  };

  const portalSettings = organization?.portal_settings || {};

  const generateCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const addResident = () => {
    setResidents([...residents, { id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }]);
  };

  const removeResident = (id: string) => {
    if (residents.length > 1) {
      setResidents(residents.filter(r => r.id !== id));
    }
  };

  const updateResident = (id: string, field: keyof ResidentInfo, value: string | boolean) => {
    setResidents(residents.map(r => 
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedApartment) {
      toast.error('Välj en lägenhet');
      return;
    }

    const validResidents = residents.filter(r => r.first_name && r.last_name && r.email);
    if (validResidents.length === 0) {
      toast.error('Fyll i minst en person med namn och e-post');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const resident of validResidents) {
      if (!emailRegex.test(resident.email)) {
        toast.error(`Ogiltig e-postadress: ${resident.email}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const code = generateCode();
      console.log('DEBUG handleSubmit: Generated code:', code);
      setExpectedEmailCode(code);
      // Save to sessionStorage to survive page reloads
      sessionStorage.setItem('portal_expectedEmailCode', code);
      console.log('DEBUG handleSubmit: Stored in sessionStorage:', sessionStorage.getItem('portal_expectedEmailCode'));

      const accessKey = crypto.randomUUID();
      
      const pendingUpdate: Omit<PendingUpdate, 'id'> = {
        organization_id: organization?.id || '',
        apartment_id: selectedApartment,
        residents: validResidents,
        verification_token: code,
        access_key: accessKey,
        email_verified: false,
        created_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };

      const result = await createDocument('member_updates', pendingUpdate);
      const resultId = (result as { id?: string }).id || (result as { data?: { id?: string } }).data?.id;
      setPendingUpdateId(resultId || null);
      // Save pendingUpdateId to sessionStorage
      if (resultId) {
        sessionStorage.setItem('portal_pendingUpdateId', resultId);
      }

      const emails = validResidents.map(r => r.email);
      const emailHtml = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 500px; margin: 0 auto; padding: 20px;">
          ${organization?.portal_settings?.logo ? 
            `<img src="${organization.portal_settings.logo}" alt="${organization?.name}" style="max-height: 60px; margin-bottom: 15px;">` : 
            ''}
          <h1 style="color: #1e293b; margin: 0;">${organization?.name}</h1>
          <p style="color: #64748b; margin-top: 8px;">Bekräfta din e-postadress</p>
          <div style="background: #f1f5f9; border-radius: 12px; padding: 24px; margin: 24px 0; text-align: center;">
            <p style="color: #64748b; margin: 0 0 12px 0; font-size: 14px;">Din verifieringskod:</p>
            <div style="font-size: 36px; font-weight: bold; letter-spacing: 8px; color: #1e293b;">${code}</div>
          </div>
          <p style="color: #94a3b8; font-size: 12px;">Koden är giltig i 24 timmar.</p>
        </div>
      `;
      
      await sendEmail(emails, `Din verifieringskod: ${code} - ${organization?.name}`, emailHtml, { fromName: organization?.name || 'Förvaltningen' });
      
      setAwaitingEmailCode(true);
      sessionStorage.setItem('portal_awaitingEmailCode', 'true');
      toast.success('Verifieringskod skickad till din e-post');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Kunde inte skicka uppgifterna');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyEmailCode = async () => {
    // Always read fresh from sessionStorage - this is the source of truth
    const storedCode = sessionStorage.getItem('portal_expectedEmailCode');
    const codeToCheck = storedCode || expectedEmailCode;
    
    // Normalize both codes: trim whitespace, convert to string
    const normalizedInput = String(emailCode || '').trim();
    const normalizedExpected = String(codeToCheck || '').trim();
    
    console.log('DEBUG verifyEmailCode:', { 
      rawInput: emailCode,
      normalizedInput,
      rawExpected: codeToCheck,
      normalizedExpected,
      fromState: expectedEmailCode,
      fromStorage: storedCode,
      match: normalizedInput === normalizedExpected
    });
    
    if (!normalizedExpected) {
      toast.error('Ingen kod hittades. Vänligen begär en ny kod.');
      return;
    }
    
    if (normalizedInput !== normalizedExpected) {
      console.log('Code mismatch - input:', normalizedInput, 'expected:', normalizedExpected);
      toast.error('Fel kod. Försök igen.');
      return;
    }

    setVerifyingEmail(true);
    
    // Try to update the document, but don't fail if it doesn't work
    if (pendingUpdateId) {
      try {
        await updateDocument('member_updates', pendingUpdateId, {
          email_verified: true
        });
      } catch (updateError) {
        console.warn('Could not update document (may lack permissions):', updateError);
        // Continue anyway - the email verification code was correct
      }
    }
    
    // These are all synchronous operations that shouldn't fail
    setEmailVerified(true);
    // Clear email verification from sessionStorage
    sessionStorage.removeItem('portal_expectedEmailCode');
    sessionStorage.removeItem('portal_awaitingEmailCode');
    toast.success('E-post verifierad!');
    
    // Check if any residents have phone numbers to verify
    const withPhone = (residents || []).filter(r => r.phone && r.phone.replace(/\D/g, '').length >= 8);
    console.log('Residents with phone:', withPhone.length);
    
    if (withPhone.length > 0) {
      setResidentsWithPhone(withPhone);
      setAskPhoneVerification(true);
    } else {
      setSubmitted(true);
    }
    
    setVerifyingEmail(false);
  };

  const sendPhoneCode = async (resident: ResidentInfo) => {
    if (!resident.phone) return;
    
    const code = generateCode();
    setExpectedPhoneCode(code);
    // Save to sessionStorage
    sessionStorage.setItem('portal_expectedPhoneCode', code);
    
    try {
      const formattedPhone = resident.phone.replace(/\D/g, '');
      const phoneWithCountry = formattedPhone.startsWith('46') ? formattedPhone : 
                               formattedPhone.startsWith('0') ? '46' + formattedPhone.slice(1) : 
                               '46' + formattedPhone;
      
      await sendSMS(phoneWithCountry, `Din verifieringskod för ${organization?.name}: ${code}`);
      setAwaitingPhoneCode(true);
      sessionStorage.setItem('portal_awaitingPhoneCode', 'true');
      toast.success(`SMS skickat till ${resident.phone}`);
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Kunde inte skicka SMS');
    }
  };

  const verifyPhoneCode = async () => {
    // Always read fresh from sessionStorage - this is the source of truth
    const storedCode = sessionStorage.getItem('portal_expectedPhoneCode');
    const codeToCheck = storedCode || expectedPhoneCode;
    
    // Normalize both codes: trim whitespace, convert to string
    const normalizedInput = String(phoneCode || '').trim();
    const normalizedExpected = String(codeToCheck || '').trim();
    
    console.log('DEBUG verifyPhoneCode:', { 
      rawInput: phoneCode,
      normalizedInput,
      rawExpected: codeToCheck,
      normalizedExpected,
      fromState: expectedPhoneCode,
      fromStorage: storedCode,
      match: normalizedInput === normalizedExpected
    });
    
    if (!normalizedExpected) {
      toast.error('Ingen kod hittades. Vänligen begär en ny kod.');
      return;
    }
    
    if (normalizedInput !== normalizedExpected) {
      console.log('Code mismatch - input:', normalizedInput, 'expected:', normalizedExpected);
      toast.error('Fel kod. Försök igen.');
      return;
    }

    setVerifyingPhone(true);
    try {
      const currentResident = residentsWithPhone[currentPhoneIndex];
      const newPhonesVerified = [...phonesVerified, currentResident.phone];
      setPhonesVerified(newPhonesVerified);
      
      if (currentPhoneIndex < residentsWithPhone.length - 1) {
        setCurrentPhoneIndex(currentPhoneIndex + 1);
        setPhoneCode('');
        setAwaitingPhoneCode(false);
        toast.success('Telefonnummer verifierat!');
      } else {
        // Clear all sessionStorage on completion
        sessionStorage.removeItem('portal_expectedPhoneCode');
        sessionStorage.removeItem('portal_awaitingPhoneCode');
        sessionStorage.removeItem('portal_pendingUpdateId');
        toast.success('Alla telefonnummer verifierade!');
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to verify phone:', error);
      toast.error('Kunde inte verifiera telefonnummer');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const skipPhoneVerification = () => {
    if (currentPhoneIndex < residentsWithPhone.length - 1) {
      setCurrentPhoneIndex(currentPhoneIndex + 1);
      setPhoneCode('');
      setAwaitingPhoneCode(false);
    } else {
      // Clear all sessionStorage on completion
      sessionStorage.removeItem('portal_expectedPhoneCode');
      sessionStorage.removeItem('portal_awaitingPhoneCode');
      sessionStorage.removeItem('portal_pendingUpdateId');
      setSubmitted(true);
    }
  };

  const skipAllPhoneVerification = () => {
    // Clear all sessionStorage on completion
    sessionStorage.removeItem('portal_expectedPhoneCode');
    sessionStorage.removeItem('portal_awaitingPhoneCode');
    sessionStorage.removeItem('portal_pendingUpdateId');
    setSubmitted(true);
  };

  // Loading state with retry info
  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm flex items-center justify-center">
          <div className="text-center px-6">
            <SpinnerGap size={36} className="animate-spin text-primary mx-auto mb-3" />
            {isRetrying && retryCount > 0 ? (
              <>
                <p className="text-slate-700 font-medium text-sm mb-1">Ansluter...</p>
                <p className="text-slate-400 text-xs">
                  Försök {retryCount + 1} av {MAX_RETRIES + 1}
                </p>
              </>
            ) : (
              <p className="text-slate-500 text-sm">Laddar...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not found state with retry option
  if (notFound) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm flex items-center justify-center p-4">
          <Card className="w-full text-center shadow-none border-0">
            <CardContent className="pt-6 pb-5">
              {connectionError ? (
                <>
                  <WifiSlash size={40} className="mx-auto text-amber-400 mb-3" />
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">Anslutningsproblem</h2>
                  <p className="text-slate-500 text-sm mb-4">
                    Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen.
                  </p>
                  <Button 
                    onClick={handleManualRetry}
                    className="gap-2"
                    data-testid="retry-button"
                  >
                    <ArrowClockwise size={18} />
                    Försök igen
                  </Button>
                </>
              ) : (
                <>
                  <Buildings size={40} className="mx-auto text-slate-300 mb-3" />
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">Föreningen hittades inte</h2>
                  <p className="text-slate-500 text-sm mb-4">
                    Kontrollera att länken är korrekt eller kontakta din förening.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={handleManualRetry}
                    className="gap-2"
                    data-testid="retry-button"
                  >
                    <ArrowClockwise size={18} />
                    Ladda om
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <SuccessScreen 
        portalSettings={portalSettings}
        organization={organization}
        onReset={() => {
          setSubmitted(false);
          setAwaitingEmailCode(false);
          setEmailCode('');
          setEmailVerified(false);
          setAskPhoneVerification(false);
          setAwaitingPhoneCode(false);
          setPhoneCode('');
          setPhonesVerified([]);
          setCurrentPhoneIndex(0);
          setResidentsWithPhone([]);
          setResidents([{ id: crypto.randomUUID(), first_name: '', last_name: '', email: '', phone: '' }]);
          setSelectedApartment('');
        }}
      />
    );
  }

  // Email verification screen
  if (awaitingEmailCode && !emailVerified) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm">
          {portalSettings.header_image && (
            <div className="h-32 sm:h-36 relative">
              <img 
                src={portalSettings.header_image} 
                alt={organization?.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          )}
          
          <div className="px-4 py-6">
            <Card className="shadow-none border-0">
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <EnvelopeSimple size={28} className="text-primary" />
                </div>
                <CardTitle className="text-lg">Verifiera din e-post</CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  Vi har skickat en 6-siffrig kod till de angivna e-postadresserna.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Ange verifieringskod</Label>
                  <Input
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.3em] font-mono h-14"
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                    data-testid="email-code-input"
                  />
                </div>
                
                <Button 
                  onClick={verifyEmailCode}
                  disabled={emailCode.length !== 6 || verifyingEmail}
                  className="w-full h-12"
                  data-testid="verify-email-button"
                >
                  {verifyingEmail ? (
                    <>
                      <SpinnerGap size={20} className="mr-2 animate-spin" />
                      Verifierar...
                    </>
                  ) : (
                    'Verifiera'
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Phone verification - ask screen
  if (askPhoneVerification && !awaitingPhoneCode && emailVerified) {
    const currentResident = residentsWithPhone[currentPhoneIndex];
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm">
          {portalSettings.header_image && (
            <div className="h-32 sm:h-36 relative">
              <img 
                src={portalSettings.header_image} 
                alt={organization?.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="px-4 py-6">
            <Card className="shadow-none border-0">
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone size={28} className="text-green-600" />
                </div>
                <CardTitle className="text-lg">Verifiera telefonnummer?</CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  {residentsWithPhone.length === 1 ? 'Telefonnummer:' : `Telefonnummer (${currentPhoneIndex + 1} av ${residentsWithPhone.length}):`}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-2">
                  <span className="font-medium">{currentResident?.first_name} {currentResident?.last_name}</span>
                  <span className="text-slate-400">—</span>
                  <span>{currentResident?.phone}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <Button 
                  onClick={() => sendPhoneCode(currentResident)}
                  className="w-full h-11"
                  data-testid="send-sms-button"
                >
                  <Phone size={18} className="mr-2" />
                  Skicka SMS-kod
                </Button>
                <Button 
                  variant="ghost"
                  onClick={skipPhoneVerification}
                  className="w-full h-10 text-slate-500"
                  data-testid="skip-phone-button"
                >
                  Hoppa över
                </Button>
                {residentsWithPhone.length > 1 && currentPhoneIndex === 0 && (
                  <Button 
                    variant="ghost"
                    onClick={skipAllPhoneVerification}
                    className="w-full h-10 text-slate-400 text-sm"
                    data-testid="skip-all-phones-button"
                  >
                    Hoppa över alla telefonnummer
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Phone verification - code entry screen
  if (awaitingPhoneCode && emailVerified) {
    const currentResident = residentsWithPhone[currentPhoneIndex];
    return (
      <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm">
          {portalSettings.header_image && (
            <div className="h-32 sm:h-36 relative">
              <img 
                src={portalSettings.header_image} 
                alt={organization?.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="px-4 py-6">
            <Card className="shadow-none border-0">
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone size={28} className="text-primary" />
                </div>
                <CardTitle className="text-lg">Ange SMS-kod</CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  Vi har skickat en kod till <strong>{currentResident?.phone}</strong>
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div>
                  <Input
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.3em] font-mono h-14"
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                    data-testid="phone-code-input"
                  />
                </div>
                
                <Button 
                  onClick={verifyPhoneCode}
                  disabled={phoneCode.length !== 6 || verifyingPhone}
                  className="w-full h-11"
                  data-testid="verify-phone-button"
                >
                  {verifyingPhone ? (
                    <>
                      <SpinnerGap size={20} className="mr-2 animate-spin" />
                      Verifierar...
                    </>
                  ) : (
                    'Verifiera'
                  )}
                </Button>
                
                <div className="flex gap-2">
                  <Button 
                    variant="ghost"
                    onClick={() => sendPhoneCode(currentResident)}
                    className="flex-1 h-10 text-sm"
                    data-testid="resend-sms-button"
                  >
                    Skicka ny kod
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={skipPhoneVerification}
                    className="flex-1 h-10 text-sm text-slate-500"
                    data-testid="skip-phone-verify-button"
                  >
                    Hoppa över
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-100 portal-mobile">
      <div className="max-w-md mx-auto bg-slate-50 min-h-screen shadow-sm">
        {/* Header */}
        {portalSettings.header_image ? (
          <div className="h-36 sm:h-40 relative">
            <img 
              src={portalSettings.header_image} 
              alt={organization?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <div className="flex items-end gap-3">
                {portalSettings.logo && (
                  <img 
                    src={portalSettings.logo} 
                    alt="Logo" 
                    className="w-11 h-11 rounded-lg bg-white shadow-lg object-contain p-1.5"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-bold text-white drop-shadow-lg truncate">
                    {organization?.name}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-primary text-white p-4">
            <div className="flex items-center gap-3">
              {portalSettings.logo && (
                <img 
                  src={portalSettings.logo} 
                  alt="Logo" 
                  className="w-11 h-11 rounded-lg bg-white shadow object-contain p-1.5"
                />
              )}
              <h1 className="text-lg sm:text-xl font-bold truncate">{organization?.name}</h1>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="px-4 py-5">
          <Card className="shadow-none border-0">
          <CardHeader className="px-4 pt-5 pb-3 sm:px-6">
            <CardTitle className="flex items-center gap-2 text-xl sm:text-lg">
              <User size={24} className="text-primary" />
              Uppdatera kontaktuppgifter
            </CardTitle>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Fyll i kontaktuppgifterna för din lägenhet.
              Du kan lägga till flera personer med + tecknet.
            </p>
          </CardHeader>
          <CardContent className="px-4 pb-5 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Apartment selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm sm:text-base font-medium">
                  <House size={18} />
                  Välj lägenhet *
                </Label>
                <Select value={selectedApartment} onValueChange={setSelectedApartment}>
                  <SelectTrigger data-testid="apartment-select" className="h-12 sm:h-11 text-base">
                    <SelectValue placeholder="Välj din lägenhet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {apartments.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id} className="text-base py-3">
                        Lägenhet {apt.apartment_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Residents */}
              <div className="space-y-4">
                <Label className="text-sm sm:text-base font-medium">Boende i lägenheten</Label>
                
                {residents.map((resident, index) => (
                  <div 
                    key={resident.id} 
                    className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">
                        Person {index + 1}
                      </span>
                      {residents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeResident(resident.id)}
                          className="text-red-500 hover:text-red-700 p-2 -mr-1 rounded-lg hover:bg-red-50 active:bg-red-100"
                        >
                          <Trash size={18} />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm sm:text-base mb-1.5 block font-medium">Förnamn *</Label>
                          <Input
                            value={resident.first_name}
                            onChange={(e) => updateResident(resident.id, 'first_name', e.target.value)}
                            placeholder="Anna"
                            data-testid={`resident-first-name-${index}`}
                            className="h-11 sm:h-10 text-base"
                          />
                        </div>
                        <div>
                          <Label className="text-sm sm:text-base mb-1.5 block font-medium">Efternamn *</Label>
                          <Input
                            value={resident.last_name}
                            onChange={(e) => updateResident(resident.id, 'last_name', e.target.value)}
                            placeholder="Andersson"
                            data-testid={`resident-last-name-${index}`}
                            className="h-11 sm:h-10 text-base"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm sm:text-base flex items-center gap-2 mb-1.5 font-medium">
                          <EnvelopeSimple size={18} />
                          E-post *
                        </Label>
                        <Input
                          type="email"
                          inputMode="email"
                          value={resident.email}
                          onChange={(e) => updateResident(resident.id, 'email', e.target.value)}
                          placeholder="anna@example.com"
                          data-testid={`resident-email-${index}`}
                          className="h-11 sm:h-10 text-base"
                        />
                        <div className="flex items-start gap-3 mt-3 p-3 bg-white rounded-lg border border-slate-100">
                          <Checkbox
                            id={`invoice-${resident.id}`}
                            checked={resident.is_invoice_recipient || false}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setResidents(residents.map(r => ({
                                  ...r,
                                  is_invoice_recipient: r.id === resident.id
                                })));
                              } else {
                                updateResident(resident.id, 'is_invoice_recipient', false);
                              }
                            }}
                            data-testid={`invoice-recipient-${index}`}
                            className="mt-0.5 h-5 w-5"
                          />
                          <label 
                            htmlFor={`invoice-${resident.id}`}
                            className="text-sm text-slate-600 cursor-pointer leading-snug"
                          >
                            <span className="flex items-center gap-2">
                              <Receipt size={16} className="text-slate-400" />
                              Skicka faktura för medlemsavgift hit
                            </span>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm sm:text-base flex items-center gap-2 mb-1.5 font-medium">
                          <Phone size={18} />
                          Mobilnummer
                        </Label>
                        <Input
                          type="tel"
                          inputMode="tel"
                          value={resident.phone}
                          onChange={(e) => updateResident(resident.id, 'phone', formatPhoneNumber(e.target.value))}
                          placeholder="070-123 45 67"
                          data-testid={`resident-phone-${index}`}
                          className="h-11 sm:h-10 text-base"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addResident}
                  className="w-full h-11 sm:h-10 text-base"
                  data-testid="add-resident-button"
                >
                  <Plus size={20} className="mr-2" />
                  Lägg till person
                </Button>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 sm:h-11 text-base font-semibold" 
                disabled={submitting}
                data-testid="submit-button"
              >
                {submitting ? (
                  <>
                    <SpinnerGap size={24} className="mr-2 animate-spin" />
                    Skickar...
                  </>
                ) : (
                  'Bekräfta uppgifter'
                )}
              </Button>

              <p className="text-sm text-slate-400 text-center leading-relaxed">
                Ett bekräftelsemail skickas till alla angivna e-postadresser.
              </p>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}
